import { Fragment, type Node as PMNode, type NodeType, type Schema } from '@milkdown/kit/prose/model';

export type PlatformTableNormalizeTypes = {
  tableHeaderRow: NodeType;
  tableRow: NodeType;
  tableHeader: NodeType;
  tableCell: NodeType;
};

const LITERAL_BR_ONLY = /^\s*<br\s*\/?>\s*$/i;

export function tableContainsPlatformRows(table: PMNode): boolean {
  let found = false;

  table.forEach((row) => {
    if (row.type.name === 'tr') {
      found = true;
    }
  });

  return found;
}

function isPlatformHeaderRow(row: PMNode): boolean {
  return row.childCount > 0 && row.content.content.every((cell) => cell.type.name === 'th');
}

function cleanLiteralBrFromInline(content: Fragment, schema: Schema): Fragment {
  const hardBreak = schema.nodes.hardbreak ?? schema.nodes.hard_break ?? schema.nodes.br;
  const next: PMNode[] = [];
  let changed = false;

  content.forEach((child) => {
    if (child.isText) {
      const text = child.text ?? '';
      // Empty-cell marker from Platform parsing Milkdown empty paragraphs.
      if (LITERAL_BR_ONLY.test(text)) {
        changed = true;

        return;
      }

      // Embedded `<br />` in cell text — convert to hard breaks; do not delete.
      if (/<br\s*\/?>/i.test(text) && hardBreak) {
        const parts = text.split(/<br\s*\/?>/i);
        for (let i = 0; i < parts.length; i++) {
          if (parts[i]) {
            next.push(schema.text(parts[i], child.marks));
          }
          if (i < parts.length - 1) {
            next.push(hardBreak.create());
          }
        }
        changed = true;

        return;
      }
    }

    if (child.isTextblock) {
      const cleanedInline = cleanLiteralBrFromInline(child.content, schema);
      if (cleanedInline !== child.content) {
        changed = true;
        next.push(child.type.create(child.attrs, cleanedInline, child.marks));

        return;
      }
    }

    next.push(child);
  });

  return changed ? Fragment.fromArray(next) : content;
}

function convertPlatformCell(cell: PMNode, asHeader: boolean, types: PlatformTableNormalizeTypes): PMNode {
  const targetType = asHeader ? types.tableHeader : types.tableCell;
  const cleanedContent = cleanLiteralBrFromInline(cell.content, cell.type.schema);

  return targetType.create(
    {
      colspan: cell.attrs.colspan ?? 1,
      rowspan: cell.attrs.rowspan ?? 1,
      alignment: cell.attrs.alignment ?? 'left',
    },
    cleanedContent,
  );
}

function createEmptyBodyRow(columnCount: number, types: PlatformTableNormalizeTypes): PMNode {
  const cells = Array.from({ length: columnCount }, () => types.tableCell.createAndFill()!);

  return types.tableRow.create(null, cells);
}

/**
 * Strip literal `<br />` text left in cells after Platform parsed Milkdown empty-paragraph markers.
 * Returns null when nothing changes.
 */
export function stripLiteralBrFromTableNode(table: PMNode): PMNode | null {
  let changed = false;
  const rows = table.content.content.map((row) => {
    let rowChanged = false;
    const cells = row.content.content.map((cell) => {
      const cleaned = cleanLiteralBrFromInline(cell.content, cell.type.schema);
      if (cleaned === cell.content) {
        return cell;
      }

      rowChanged = true;

      return cell.type.create(cell.attrs, cleaned, cell.marks);
    });

    if (!rowChanged) {
      return row;
    }

    changed = true;

    return row.type.create(row.attrs, cells, row.marks);
  });

  return changed ? table.type.create(table.attrs, rows, table.marks) : null;
}

/**
 * Rewrites Platform `tr`/`th`/`td` subtrees to Milkdown `table_*` nodes.
 * Returns null when the table is already Milkdown-shaped.
 */
export function normalizePlatformTableNode(table: PMNode, types: PlatformTableNormalizeTypes): PMNode | null {
  if (!tableContainsPlatformRows(table)) {
    return null;
  }

  let isFirstPlatformRow = true;
  const rows: PMNode[] = [];

  for (const row of table.content.content) {
    if (row.type.name !== 'tr') {
      rows.push(row);
      continue;
    }

    const asHeaderRow = isFirstPlatformRow && isPlatformHeaderRow(row);
    isFirstPlatformRow = false;
    const rowType = asHeaderRow ? types.tableHeaderRow : types.tableRow;
    const cells = row.content.content.map((cell) => convertPlatformCell(cell, asHeaderRow, types));

    rows.push(rowType.create(null, cells));
  }

  const hasBodyRow = rows.some((row) => row.type.name === 'table_row');
  if (!hasBodyRow) {
    const headerRow = rows.find((row) => row.type.name === 'table_header_row');
    const columnCount = headerRow?.childCount ?? 1;
    rows.push(createEmptyBodyRow(columnCount, types));
  }

  return table.type.create(table.attrs, rows);
}
